import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import HelpDrawer from "../common/HelpDrawer";
import normalizeHelpSections from "../common/help/normalizeHelpSections";

export default function ReportHelpCard({
  title,
  subtitle,
  sections,
  onJumpToSection,
  troubleshooting = [],
  showButton = true,
  openSignal = 0,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const drawerTitle = useMemo(() => {
    const base = String(title || "").replace(/^Help Guide:\s*/i, "").trim();
    if (!base) return "Help Guide";
    return /guide$/i.test(base) ? base : `${base} Guide`;
  }, [title]);

  const drawerSections = useMemo(() => normalizeHelpSections(sections), [sections]);
  const introText =
    subtitle ||
    "Use this guide to understand this page and jump to the right section quickly.";

  useEffect(() => {
    if (!openSignal) return;
    setIsOpen(true);
  }, [openSignal]);

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:px-5 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="text-sm md:text-base font-semibold text-slate-900">
              Need help with this page?
            </div>
            <div className="text-sm text-slate-600">{introText}</div>
          </div>
          {showButton ? (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="h-9 px-4 rounded-full border border-slate-300 bg-white text-xs md:text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Help
            </button>
          ) : null}
        </div>
      </div>

      <HelpDrawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={drawerTitle}
        intro={introText}
        sections={drawerSections}
        troubleshooting={troubleshooting}
        onJumpToSection={onJumpToSection}
      />
    </>
  );
}

ReportHelpCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      heading: PropTypes.string,
      description: PropTypes.string,
      bullets: PropTypes.arrayOf(PropTypes.string),
      tips: PropTypes.arrayOf(PropTypes.string),
      anchorId: PropTypes.string,
    })
  ),
  onJumpToSection: PropTypes.func,
  showButton: PropTypes.bool,
  openSignal: PropTypes.number,
  troubleshooting: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
    })
  ),
};
